/* SpawnAnything — sound controls: a small speaker button in the playback bar (next to "Stil") and in the setup panel
   (next to "Grafik"), opening a popover with mute and volume sliders (Gesamt / Musik / Effekte). Settings persist via the
   engine (localStorage). M toggles mute. Every button/select click gets a soft UI click sound. */

const CSS = `
.aw-snd{display:inline-flex;align-items:center;justify-content:center;gap:6px}
.aw-snd svg{width:18px;height:18px;display:block}
.aw-snd[aria-pressed=true]{color:var(--muted)}
.aw-sndrow{display:grid;grid-template-columns:62px 1fr;gap:10px;align-items:center}
.aw-sndrow label{font-size:12px;font-weight:600;letter-spacing:.06em;text-transform:uppercase}
.aw-sndrow .ghost{justify-self:start}
.aw-sndpop{position:fixed;z-index:12;width:240px;box-sizing:border-box;background:var(--panel-solid,#121822);border:1px solid var(--line,rgba(205,220,255,.13));border-radius:8px;padding:12px;display:flex;flex-direction:column;gap:10px;box-shadow:0 12px 30px rgba(0,0,0,.45)}
.aw-sndpop .hd{display:flex;align-items:center;justify-content:space-between;font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:var(--muted,#9ba7b9)}
.aw-sndpop .r{display:grid;grid-template-columns:62px 1fr 34px;gap:8px;align-items:center;font-size:13px;font-weight:600}
.aw-sndpop input[type=range]{width:100%;accent-color:var(--gold,#ffc93c)}
.aw-sndpop output{font-family:var(--f-mono,monospace);font-size:12px;text-align:right}
.aw-sndpop .ghost{background:var(--tint,rgba(255,255,255,.05));border:1px solid var(--line);border-radius:5px;padding:6px 10px;font-weight:700;font-size:13px}
.aw-sndpop .ghost[aria-pressed=true]{border-color:var(--gold);background:rgba(255,201,60,.12);color:var(--gold)}
.aw-sndpop .note{font-size:12px;color:var(--muted,#9ba7b9);margin:0}
`;
const ICON_ON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 9h4l5-4v14l-5-4H3z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
const ICON_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 9h4l5-4v14l-5-4H3z"/><path d="M16 9l6 6M22 9l-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

export function mountSoundPanel(engine) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  const S = engine.settings;
  const buttons = [];
  const mkBtn = (cls, label) => {
    const b = document.createElement('button');
    b.className = 'aw-snd ' + cls; b.type = 'button'; b.title = 'Ton und Musik (Taste M schaltet stumm)';
    b.setAttribute('aria-haspopup', 'dialog'); b.setAttribute('aria-expanded', 'false');
    b.dataset.label = label || '';
    b.addEventListener('click', e => { e.stopPropagation(); toggle(b); });
    buttons.push(b); return b;
  };
  // playback bar: right after the style buttons
  const ctrl = document.getElementById('ctrl');
  if (ctrl) { const b = mkBtn(''); const tour = document.getElementById('tour'); if (tour && tour.parentNode === ctrl) tour.after(b); else ctrl.appendChild(b); }
  // setup panel: a "Ton" row under "Grafik"
  const q = document.getElementById('quality'), qrow = q && q.closest('.qrow');
  if (qrow) {
    const row = document.createElement('div'); row.className = 'aw-sndrow';
    const l = document.createElement('label'); l.textContent = 'Ton';
    row.append(l, mkBtn('ghost', 'Lautstärke')); qrow.after(row);
  }

  const pop = document.createElement('div');
  pop.className = 'aw-sndpop'; pop.hidden = true; pop.setAttribute('role', 'dialog'); pop.setAttribute('aria-label', 'Ton und Musik');
  pop.innerHTML = `<div class="hd"><span>Ton &amp; Musik</span><button class="ghost" data-k="mute" aria-pressed="false"></button></div>
    <div class="r"><label for="awVolM">Gesamt</label><input type="range" id="awVolM" data-k="master" min="0" max="100" step="1"><output></output></div>
    <div class="r"><label for="awVolMu">Musik</label><input type="range" id="awVolMu" data-k="music" min="0" max="100" step="1"><output></output></div>
    <div class="r"><label for="awVolS">Effekte</label><input type="range" id="awVolS" data-k="sfx" min="0" max="100" step="1"><output></output></div>
    <p class="note">Taste M schaltet den Ton stumm.</p>`;
  document.body.appendChild(pop);
  const muteBtn = pop.querySelector('[data-k=mute]');
  pop.querySelectorAll('input[type=range]').forEach(inp => {
    inp.addEventListener('input', () => { engine.set(inp.dataset.k, inp.value / 100); });
  });
  muteBtn.addEventListener('click', () => engine.toggleMute());
  pop.addEventListener('pointerdown', e => e.stopPropagation());

  function sync() {
    for (const b of buttons) {
      b.innerHTML = (S.muted ? ICON_OFF : ICON_ON) + (b.dataset.label ? `<span>${S.muted ? 'Stumm' : b.dataset.label}</span>` : '');
      b.setAttribute('aria-pressed', String(S.muted));
      b.setAttribute('aria-label', S.muted ? 'Ton ist aus – Einstellungen öffnen' : 'Ton und Musik einstellen');
    }
    muteBtn.textContent = S.muted ? 'Ton an' : 'Stumm'; muteBtn.setAttribute('aria-pressed', String(S.muted));
    pop.querySelectorAll('input[type=range]').forEach(inp => { inp.value = Math.round(S[inp.dataset.k] * 100); inp.nextElementSibling.textContent = inp.value; inp.disabled = S.muted; });
  }
  let owner = null;
  function place(b) {
    const r = b.getBoundingClientRect(), w = 240, h = pop.offsetHeight || 170;
    let x = Math.min(innerWidth - w - 8, Math.max(8, r.left + r.width / 2 - w / 2));
    let y = r.top - h - 8; if (y < 8) y = r.bottom + 8;
    pop.style.left = x + 'px'; pop.style.top = y + 'px';
  }
  function toggle(b) {
    if (!pop.hidden && owner === b) { close(); return; }
    owner = b; pop.hidden = false; sync(); place(b);
    buttons.forEach(x => x.setAttribute('aria-expanded', String(x === b)));
    pop.querySelector('input').focus({ preventScroll: true });
  }
  function close() { pop.hidden = true; buttons.forEach(x => x.setAttribute('aria-expanded', 'false')); if (owner && owner.isConnected) owner.focus({ preventScroll: true }); owner = null; }
  addEventListener('pointerdown', () => { if (!pop.hidden) { pop.hidden = true; buttons.forEach(x => x.setAttribute('aria-expanded', 'false')); owner = null; } });
  addEventListener('resize', () => { if (!pop.hidden && owner) place(owner); });
  addEventListener('keydown', e => {
    if (e.code === 'Escape' && !pop.hidden) { e.preventDefault(); close(); return; } // main.js skips a used Esc
    const tag = (e.target && e.target.tagName) || '';
    if (e.code === 'KeyM' && !e.repeat && !e.ctrlKey && !e.metaKey && !e.altKey && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') engine.toggleMute();
  }, true);
  // soft click for every UI button / select
  document.addEventListener('click', e => { const el = e.target && e.target.closest && e.target.closest('button, select, .chip'); if (el && !el.disabled) engine.ui('click', 0.25); }, true);
  document.addEventListener('change', e => { if (e.target && e.target.tagName === 'SELECT') engine.ui('click', 0.2); }, true);
  engine.onChange(sync);
  sync();
}
